import React from "react";
import { BarTask } from "../../types/bar-task";
import { Dependency } from "../../types/public-types";

type ArrowProps = {
  taskFrom: BarTask;
  taskTo: BarTask;
  rowHeight: number;
  taskHeight: number;
  arrowIndent: number;
  arrowColor: string;
  dependencyType: Dependency;
};

export const Arrow: React.FC<ArrowProps> = ({
  taskFrom,
  taskTo,
  rowHeight,
  taskHeight,
  arrowIndent,
  arrowColor,
  dependencyType,
}) => {
  let [path, trianglePoints] = drawPathAndTriangle(
    taskFrom,
    taskTo,
    rowHeight,
    taskHeight,
    arrowIndent,
    dependencyType
  );

  return (
    <g className="arrow">
      <path strokeWidth="1.5" d={path} fill="none" stroke={arrowColor} />
      <polygon points={trianglePoints} fill={arrowColor} />
    </g>
  );
};

const drawPathAndTriangle = (
  taskFrom: BarTask,
  taskTo: BarTask,
  rowHeight: number,
  taskHeight: number,
  arrowIndent: number,
  dependencyType: Dependency
) => {
  const indexCompare = taskFrom.index > taskTo.index ? -1 : 1;
  const taskToEndY = taskTo.y + taskHeight / 2;
  const verticalOffset = indexCompare * (rowHeight / 2);

  // 1️⃣ Pick start/end x-points based on dependency type
  const minX = (t: BarTask) => {
    // Use visual segment edge when available (correct for working-time bars in day view)
    if (t.plannedSegments && t.plannedSegments.length > 0) {
      const p = t.plannedSegments[0].x1;
      if (t.actualSegments && t.actualSegments.length > 0) return Math.min(p, t.actualSegments[0].x1);
      return p;
    }
    if (t.actualSegments && t.actualSegments.length > 0) return t.actualSegments[0].x1;
    // Only blend a coord when that bar actually exists (positive width). A bar
    // whose dates are the 1970 epoch sentinel clamps its x-coords to 0 (the
    // chart's left edge); a `>= 0` check would let that 0 drag the endpoint left.
    const candidates: number[] = [];
    if (t.x2 > t.x1) candidates.push(t.x1);
    if (t.actualx2 > t.actualx1) candidates.push(t.actualx1);
    return candidates.length > 0 ? Math.min(...candidates) : 0;
  };
  const maxX = (t: BarTask) => {
    if (t.plannedSegments && t.plannedSegments.length > 0) {
      const p = t.plannedSegments[t.plannedSegments.length - 1].x2;
      if (t.actualSegments && t.actualSegments.length > 0) return Math.max(p, t.actualSegments[t.actualSegments.length - 1].x2);
      return p;
    }
    if (t.actualSegments && t.actualSegments.length > 0) return t.actualSegments[t.actualSegments.length - 1].x2;
    // Mirror minX: only blend a coord when that bar actually exists (positive
    // width), so a 1970-epoch sentinel bar (x-coords clamped to 0) can't apply.
    const candidates: number[] = [];
    if (t.x2 > t.x1) candidates.push(t.x2);
    if (t.actualx2 > t.actualx1) candidates.push(t.actualx2);
    return candidates.length > 0 ? Math.max(...candidates) : 0;
  };

  let fromPoint: number, toPoint: number;
  switch (dependencyType) {
    case "SS":
      fromPoint = minX(taskFrom);
      toPoint = minX(taskTo);
      break;
    case "SF":
      fromPoint = minX(taskFrom);
      toPoint = maxX(taskTo);
      break;
    case "FS":
      fromPoint = maxX(taskFrom);
      toPoint = minX(taskTo);
      break;
    case "FF":
      fromPoint = maxX(taskFrom);
      toPoint = maxX(taskTo);
      break;
  }

  // 2️⃣ Helper for arrowhead points
  const arrowPoints = (x: number, y: number, right = true) =>
    right
      ? `${x},${y} ${x - 5},${y - 5} ${x - 5},${y + 5}` // Right
      : `${x},${y} ${x + 5},${y - 5} ${x + 5},${y + 5}`; // Left

  let path: string;
  let trianglePoints: string;

  // 3️⃣ Routing based on type
  switch (dependencyType) {
    case "SS":
      path = `M ${fromPoint} ${taskFrom.y + taskHeight / 2}
              H ${
                fromPoint + Math.min(toPoint - fromPoint, 0) - 2 * arrowIndent
              }
              V ${taskToEndY}
              H ${toPoint - 5}`;
      trianglePoints = arrowPoints(toPoint, taskToEndY, true);
      break;

    case "SF":
      path = `M ${fromPoint} ${taskFrom.y + taskHeight / 2}
              H ${fromPoint - 2 * arrowIndent}
              V ${taskFrom.y + taskHeight / 2 + verticalOffset}
                ${
                  fromPoint - toPoint > 4 * arrowIndent
                    ? ""
                    : `H ${toPoint + 2 * arrowIndent}`
                }
              V ${taskToEndY}
              H ${toPoint + 5}`;
      trianglePoints = arrowPoints(toPoint, taskToEndY, false);
      break;

    case "FS":
      path = `M ${fromPoint} ${taskFrom.y + taskHeight / 2}
              H ${fromPoint + 2 * arrowIndent}
              V ${taskFrom.y + taskHeight / 2 + verticalOffset}
                ${
                  toPoint - fromPoint > 4 * arrowIndent
                    ? ""
                    : `H ${toPoint - 2 * arrowIndent}`
                }
              V ${taskToEndY}
              H ${toPoint - 5}`;
      trianglePoints = arrowPoints(toPoint, taskToEndY, true);
      break;

    case "FF":
      path = `M ${fromPoint} ${taskFrom.y + taskHeight / 2}
              H ${
                fromPoint + Math.max(toPoint - fromPoint, 0) + 2 * arrowIndent
              }
              V ${taskToEndY}
              H ${toPoint + 5}`;
      trianglePoints = arrowPoints(toPoint, taskToEndY, false);
      break;
  }

  return [path, trianglePoints];
};
